<script lang='ts' setup>
const refreshUser = useState<boolean>('refreshUser', () => false);
const { user, fetch: refreshSession, clear: clearSession } = useUserSession();

const props = defineProps({
	navType: {
		type: String as () => 'auth' | 'profile' | 'website',
		required: true,
		default: 'website'
	},
	showSidebarToggle: {
		type: Boolean,
		default: false
	}
});

const logout = async () => {
	await clearSession();
	await navigateTo('/');
};

type NavItemsList = {
	label?: string;
	description?: string;
	icon?: string;
	to?: string;
	slot?: string;
	onSelect?: () => void;
	children?: NavItemsList;
}[];

const homeItems: NavItemsList = [
	{
		label: 'Home',
		icon: 'i-lucide-home',
		to: '/'
	},
	{
		label: 'Pricing',
		to: '/pricing'
	},
	{
		label: 'Docs',
		icon: 'i-lucide-book',
		to: '/docs'
	}
];

const websiteItems: NavItemsList = props.showSidebarToggle ? [
	{
		label: 'Menu',
		slot: 'sidebar-toggle'
	}
] : [
	{
		label: 'Home',
		icon: 'i-lucide-home',
		to: '/'
	},
	{
		label: 'Reports',
		icon: 'i-lucide-file',
		to: '/report'
	},
	{
		label: 'Docs',
		icon: 'i-lucide-book',
		to: '/docs'
	}
];

const authItems: NavItemsList = [
	{
		label: 'ColorMode',
		slot: 'colormode'
	},
	{
		label: 'Login',
		icon: 'i-lucide-log-in',
		to: '/login'
	},
	{
		label: 'Signup',
		icon: 'i-lucide-user-plus',
		to: '/signup'
	}
];

const profileItems: NavItemsList = [
	{
		slot: 'profile'
	}
];

const items = computed(() => {
	switch (props.navType) {
		case 'auth':
			return [homeItems, authItems];
		case 'website':
			return [websiteItems, profileItems];
		default:
			return [];
	}
});

const route = useRoute();

const active = computed(() => {
	const path = route.path;
	return items.value.findIndex((group) => group.some((item) => item.to === path)).toString();
});

watch(refreshUser, () => {
	if (refreshUser.value) {
		refreshSession();
		refreshUser.value = false;
	}
});

const isProfilePopoverOpen = ref(false);
</script>

<template>
	<UNavigationMenu class="w-full h-(--ui-header-height) px-1 shadow-md backdrop-blur fixed top-0 z-50" color="primary"
		v-model="active" :items="items">
		<template #sidebar-toggle>
			<ClientOnly>
				<SidebarToggleButton class="md:hidden" />
			</ClientOnly>
		</template>

		<template #colormode>
			<ColorModeButton />
		</template>

		<template #profile-trailing>
			<ClientOnly>
				<UPopover :mode="$device.isMobileOrTablet ? 'click' : 'hover'" v-model:open="isProfilePopoverOpen">
					<UButton variant="link" color="neutral" :label="user?.username || 'User'"
						:trailing-icon="isProfilePopoverOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" />

					<template #content>
						<div class="flex flex-col p-3 space-y-4 min-w-64">
							<div class="flex items-center space-x-3">
								<div class="h-9 w-9 rounded-full bg-accented flex items-center justify-center font-semibold">
									{{ (user?.username || 'U').charAt(0).toUpperCase() }}
								</div>
								<div class="flex flex-col">
									<span class="text-sm font-semibold">{{ user?.username }}</span>
									<span class="text-xs opacity-70">{{ user?.email }}</span>
								</div>
								<div class="ml-auto">
									<ColorModeButton />
								</div>
							</div>
							<UButton to="/profile" variant="soft" color="neutral" icon="i-lucide-user" label="Profile" size="xs" />
							<div class="space-y-2">
								<div class="text-xs uppercase tracking-wide opacity-70 px-1">Website</div>
								<WebsiteSwitcher />
							</div>
							<div class="pt-2 border-t border-accented flex">
								<UButton variant="soft" color="neutral" icon="i-lucide-log-out" label="Logout" @click="logout" />
							</div>
						</div>
					</template>
				</UPopover>
			</ClientOnly>
		</template>

	</UNavigationMenu>
</template>
